// Sprint 1 — WhatsApp Sales Agent Demo (simulator).
// Sprint 2 PR-D2:
//   - Persisted IDs panel (PR-C2 response shape: persisted, idempotent, requestId,
//     eventId, conversationId, agentRunId, leadAnalysisId, actionId, approvalId,
//     quoteDraftId)
//   - "Detay →" buttons → page-id geçişi (cb-actions, cb-approvals, cb-conversations,
//     cb-lead-analyses, cb-quote-drafts, event-inbox); URL state YOK (in-memory only).
//   - Son 5 run in-memory history (refresh sıfırlar).
//   - Quote Drafts CTA pre-fill: pendingSectorPrefill → sector=quote.
//   - Ham JSON dump kaldırılmadı (en altta küçük details). UX yapılandırıldı.

import { escHtml } from '../utils.js';
import { consumeSectorPrefill } from './cb-quote-drafts.js';

interface PersistedIds {
  persisted?: boolean;
  idempotent?: boolean;
  requestId?: string;
  eventId?: string;
  conversationId?: string;
  agentRunId?: string;
  leadAnalysisId?: string;
  actionId?: string;
  approvalId?: string;
  quoteDraftId?: string | null;
}

interface SimulateResponse extends PersistedIds {
  ok: boolean;
  error?: string;
  analysis?: { sector?: string; intent?: string; score?: string; summary?: string; suggestedReply?: string; missingFields?: string[]; nextAction?: string };
  context?: { found?: boolean; source?: string | null; tokenEstimate?: number };
  [k: string]: unknown;
}

interface HistoryEntry {
  ts: string;
  requestId: string;
  sector: string;
  intent: string;
  persisted: boolean;
  idempotent: boolean;
}

let lastResult: SimulateResponse | null = null;
const history: HistoryEntry[] = [];
const HISTORY_LIMIT = 5;

export function renderWhatsappDemo(): void {
  const root = document.getElementById('view-whatsapp-demo');
  if (!root) return;

  // Quote Drafts CTA'dan pre-fill geldi mi?
  const prefilledSector = consumeSectorPrefill();

  root.innerHTML = `
    <div class="view-header">
      <div>
        <div class="view-title">WhatsApp Sales Agent Demo</div>
        <div class="view-sub">Simulator — reklamdan gelen mesaj akışını test et (PR-C2: central_brain'e persist)</div>
      </div>
    </div>
    <div class="rt-card" style="display:grid;gap:10px;">
      <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;">
        <label>Sektör
          <select id="wa-sector" class="form-select" style="width:100%;">
            <option value="beauty"${prefilledSector === 'beauty' ? ' selected' : ''}>beauty</option>
            <option value="quote"${prefilledSector === 'quote' ? ' selected' : ''}>quote</option>
            <option value="project"${prefilledSector === 'project' ? ' selected' : ''}>project</option>
          </select>
        </label>
        <label>Tenant slug
          <select id="wa-tenant" class="form-select" style="width:100%;">
            <option value="demo-beauty">demo-beauty</option>
            <option value="demo-quote">demo-quote</option>
          </select>
        </label>
        <label>Telefon (ops.)
          <input id="wa-phone" class="form-input" placeholder="+905..." style="width:100%;" />
        </label>
      </div>
      <label>Müşteri mesajı
        <textarea id="wa-message" class="form-textarea" rows="3" placeholder="örn: lazer epilasyon fiyatı nedir?"></textarea>
      </label>
      <div style="display:flex;gap:8px;">
        <button id="wa-run-btn" class="btn-primary" style="padding:8px 16px;">Analiz Et + Persist</button>
        <button id="wa-clear-btn" class="btn-icon" style="padding:8px 12px;">Temizle</button>
      </div>
    </div>

    ${renderResultPanels(lastResult)}

    <div class="rt-card" style="margin-top:12px;">
      <div class="rt-card-title">Son ${HISTORY_LIMIT} run (in-memory)</div>
      ${history.length === 0
        ? '<div style="color:var(--muted);font-size:12px;">Henüz çalıştırma yok</div>'
        : `<table style="width:100%;font-size:12px;">
            <thead><tr><th style="text-align:left;">Zaman</th><th style="text-align:left;">Sector</th><th style="text-align:left;">Intent</th><th style="text-align:left;">RequestID</th><th style="text-align:left;">Persisted</th></tr></thead>
            <tbody>${history.map(h => `
              <tr>
                <td style="color:var(--muted);">${escHtml(h.ts)}</td>
                <td><code>${escHtml(h.sector)}</code></td>
                <td>${escHtml(h.intent)}</td>
                <td><code style="font-size:10px;">${escHtml(h.requestId.slice(0, 16))}</code></td>
                <td>${h.persisted ? (h.idempotent ? '♻️ idempotent' : '✅') : '❌'}</td>
              </tr>`).join('')}
            </tbody>
          </table>`
      }
    </div>
  `;
  attachHandlers();
}

function renderResultPanels(r: SimulateResponse | null): string {
  if (!r) {
    return `<div class="rt-card" style="margin-top:12px;">
      <div class="rt-card-title">Analiz Sonucu</div>
      <div class="empty-state"><div class="empty-icon">💬</div><div class="empty-title">Henüz analiz yok</div></div>
    </div>`;
  }
  if (!r.ok || r.error) {
    return `<div class="rt-card" style="margin-top:12px;border-left:3px solid var(--danger,#e55);">
      <div class="rt-card-title">Hata</div>
      <div style="color:var(--muted);font-size:13px;">${escHtml(r.error ?? 'unknown')}</div>
    </div>`;
  }
  return `${renderAnalysisPanel(r)}${renderPersistedPanel(r)}`;
}

function renderAnalysisPanel(r: SimulateResponse): string {
  const a = r.analysis ?? {};
  const ctx = r.context ?? {};
  return `<div class="rt-card" style="margin-top:12px;">
    <div class="rt-card-title">Analiz</div>
    <div style="display:grid;grid-template-columns:auto 1fr;gap:4px 12px;font-size:13px;margin-bottom:10px;">
      <span style="color:var(--muted);">sector</span><code>${escHtml(a.sector ?? '—')}</code>
      <span style="color:var(--muted);">intent</span><code>${escHtml(a.intent ?? '—')}</code>
      <span style="color:var(--muted);">score</span><code>${escHtml(a.score ?? '—')}</code>
      <span style="color:var(--muted);">context</span><span>${ctx.found ? `✓ ${escHtml(ctx.source ?? '')} (${ctx.tokenEstimate ?? 0} tok)` : '✗ no context'}</span>
    </div>
    ${a.summary ? `<div style="background:var(--bg2);padding:8px;border-radius:4px;font-size:13px;margin-bottom:6px;"><strong>Özet:</strong> ${escHtml(a.summary)}</div>` : ''}
    ${a.suggestedReply ? `<div style="background:var(--bg2);padding:8px;border-radius:4px;font-size:13px;margin-bottom:6px;"><strong>Önerilen cevap:</strong> ${escHtml(a.suggestedReply)}</div>` : ''}
    ${a.missingFields && a.missingFields.length > 0 ? `<div style="font-size:12px;"><strong>Eksik:</strong> ${a.missingFields.map(m => `<code style="background:var(--bg2);padding:1px 6px;border-radius:4px;margin-right:3px;">${escHtml(m)}</code>`).join('')}</div>` : ''}
    <details style="margin-top:10px;"><summary style="cursor:pointer;font-size:11px;color:var(--muted);">ham JSON</summary>
      <pre style="background:var(--bg2);padding:8px;border-radius:4px;font-size:11px;white-space:pre-wrap;">${escHtml(JSON.stringify(r, null, 2))}</pre>
    </details>
  </div>`;
}

export function renderPersistedPanel(r: SimulateResponse): string {
  const persisted = r.persisted === true;
  const idem = r.idempotent === true;
  return `<div class="rt-card" style="margin-top:12px;">
    <div class="rt-card-title">Persistence IDs ${persisted ? '✅' : '❌'}${idem ? ' · ♻️ idempotent' : ''}</div>
    <div style="display:grid;grid-template-columns:auto 1fr auto;gap:6px 12px;font-size:12px;">
      ${idRow('requestId', r.requestId, null)}
      ${idRow('eventId', r.eventId, 'event-inbox', 'Event Inbox')}
      ${idRow('agentRunId', r.agentRunId, null)}
      ${idRow('conversationId', r.conversationId, 'cb-conversations', 'Conversations')}
      ${idRow('leadAnalysisId', r.leadAnalysisId, 'cb-lead-analyses', 'Lead Analyses')}
      ${idRow('actionId', r.actionId, 'cb-actions', 'Actions')}
      ${idRow('approvalId', r.approvalId, 'cb-approvals', 'Approvals')}
      ${idRow('quoteDraftId', r.quoteDraftId ?? null, 'cb-quote-drafts', 'Quote Drafts')}
    </div>
  </div>`;
}

function idRow(label: string, id: string | null | undefined, targetPage: string | null, linkLabel?: string): string {
  if (!id) {
    return `<span style="color:var(--muted);">${escHtml(label)}</span><span style="color:var(--muted);font-style:italic;">—</span><span></span>`;
  }
  const link = targetPage && linkLabel
    ? `<button class="btn-icon" data-action="wa-detail" data-target-page="${escHtml(targetPage)}" style="padding:2px 8px;font-size:11px;">${escHtml(linkLabel)} →</button>`
    : '';
  return `<span style="color:var(--muted);">${escHtml(label)}</span><code style="font-size:11px;">${escHtml(id)}</code>${link}`;
}

async function runSimulation(): Promise<void> {
  const sector = (document.getElementById('wa-sector') as HTMLSelectElement | null)?.value;
  const tenantSlug = (document.getElementById('wa-tenant') as HTMLSelectElement | null)?.value;
  const phone = (document.getElementById('wa-phone') as HTMLInputElement | null)?.value;
  const message = (document.getElementById('wa-message') as HTMLTextAreaElement | null)?.value;
  if (!sector || !tenantSlug || !message) return;

  try {
    const r = await fetch('/api/v2/whatsapp/simulate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenantId: tenantSlug, tenantSlug, sector, message, phone }),
    });
    const j = await r.json() as SimulateResponse;
    lastResult = j;
    if (j.ok && j.requestId) {
      history.unshift({
        ts: new Date().toISOString().slice(11, 19),
        requestId: j.requestId,
        sector,
        intent: j.analysis?.intent ?? '—',
        persisted: !!j.persisted,
        idempotent: !!j.idempotent,
      });
      if (history.length > HISTORY_LIMIT) history.length = HISTORY_LIMIT;
    }
  } catch (err) {
    lastResult = { ok: false, error: (err as Error).message };
  }
  renderWhatsappDemo();
}

function attachHandlers(): void {
  document.getElementById('wa-run-btn')?.addEventListener('click', () => { void runSimulation(); });
  document.getElementById('wa-clear-btn')?.addEventListener('click', () => { lastResult = null; renderWhatsappDemo(); });

  // Detay → page-id geçişi (in-memory only, URL state yok)
  const root = document.getElementById('view-whatsapp-demo');
  if (!root) return;
  root.addEventListener('click', (e) => {
    const target = e.target as HTMLElement | null;
    const btn = target?.closest<HTMLElement>('[data-action="wa-detail"]');
    if (!btn) return;
    const page = btn.dataset.targetPage;
    if (page) document.getElementById(`nav-${page}`)?.click();
  });
}

export function loadWhatsappDemo(): void {
  // No-op: render on demand
}
